import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import { RootState, MyThunkDispatch } from 'store';

import { fetchHasAccess } from 'actions/accessLog';
import { fetchGroupList } from 'actions/group';
import { fetchRule, saveRule, deleteRule } from 'actions/rules';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Response } from 'models/response';
import { RuleResponse, RuleForm } from 'models/rule';
import Rule from 'screens/components/Rule';
import { extractErrorMessage } from 'utils/generic';

interface Params {
  match: {
    params: {
      ruleId: string;
    };
  };
  isFromSelection?: boolean;
  originGroup?: string;
  onCloseFormRule?: () => void;
}

const RuleContainer = ({ match: { params }, isFromSelection, onCloseFormRule, originGroup }: Params) => {
  const toast = useToast();
  const history = useHistory();
  const { t } = useStoreTranslation();
  const dispatch: MyThunkDispatch = useDispatch();
  const { updateItemCreated, itemCreated } = useSelectionOrigin();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rule, setRule] = useState<RuleResponse>();

  const { groupList, isLoading: groupsLoading } = useSelector((state: RootState) => state.group);
  const { accountResult } = useSelector((state: RootState) => state.user);
  if (!accountResult) return null;
  const { countryCurrency, countryCulture, companyId } = accountResult;

  useEffect(() => {
    dispatch(fetchGroupList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, itemCreated]);

  useEffect(() => {
    if (params.ruleId) {
      setIsLoading(true);
      fetchRule(params.ruleId)
        .then(({ data: { data } }: AxiosResponse<Response<RuleResponse>>) => {
          setRule(data);
        })
        .catch((ex) => {
          toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
        })
        .finally(() => setIsLoading(false));
    }
    const screenName = 'RulesCreateTour';
    dispatch(fetchHasAccess(screenName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.ruleId]);

  const handleSave = (data: RuleForm) => {
    setIsSaving(true);
    saveRule(data, rule)
      .then(({ data: { data } }: any) => {
        if (isFromSelection) {
          updateItemCreated([data]);
          return;
        }
        if (!rule) {
          setShowSuccess(true);
          return;
        }
        history.push('/rules');
      })
      .catch((ex) => {
        setIsSaving(false);
        toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
      });
  };

  const handleDelete = (data: RuleForm) => {
    setIsLoading(true);
    deleteRule(params.ruleId)
      .then(() => {
        history.push('/rules');
        toast.add({ type: 'success', message: t('rule.delete.successMessage', { name: data.name }) });
      })
      .catch((ex) => {
        setIsLoading(false);
        toast.add({ type: 'danger', message: extractErrorMessage(ex) || t('defaultError.default') });
      });
  };

  return (
    <Rule
      rule={rule}
      originGroup={originGroup}
      isFromSelection={isFromSelection}
      groupList={groupList}
      loading={isLoading || groupsLoading}
      saving={isSaving}
      countryCurrency={countryCurrency}
      countryCulture={countryCulture}
      onSave={handleSave}
      onDelete={handleDelete}
      onCloseFormRule={onCloseFormRule}
      showSuccess={showSuccess}
    />
  );
};

export default RuleContainer;
